import { useState, useCallback } from 'react';
import { FiFileText, FiShoppingCart, FiRefreshCw, FiDownload, FiCheckCircle, FiDollarSign, FiCalendar } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import type { HomeFeedFilter } from './useHomeFeed';
import { ORDER_ACTION_ICONS } from '../utils/orderActionIcons';

export interface TabDefinition {
    value: HomeFeedFilter;
    label: string;
    icon: IconType;
    requiresRekeying?: boolean;
}

export interface TabConfigItem {
    value: HomeFeedFilter;
    visible: boolean;
}

export const ALL_TABS: TabDefinition[] = [
    { value: 'needs-quotes', label: 'Needs Quotes', icon: FiFileText },
    { value: 'unordered-parts', label: 'Unordered Parts', icon: FiShoppingCart },
    { value: 'supplier-changes', label: 'Supplier Changes', icon: FiRefreshCw },
    { value: 'in-review', label: 'In Review', icon: FiDownload, requiresRekeying: true },
    { value: 'completed', label: 'Completed', icon: FiCheckCircle },
    { value: 'manually-flagged', label: 'Saved for Later', icon: ORDER_ACTION_ICONS.flag },
    { value: 'totals-and-quotes', label: 'Totals & Quotes', icon: FiDollarSign },
    { value: 'scheduled', label: 'Scheduled', icon: FiCalendar },
];

const DEFAULT_TAB_CONFIG: TabConfigItem[] = ALL_TABS.map(t => ({ value: t.value, visible: true }));

const getStorageKey = (pageKey: string) => `tab_config_${pageKey}`;

const loadTabConfig = (pageKey: string): TabConfigItem[] => {
    try {
        const saved = localStorage.getItem(getStorageKey(pageKey));
        if (saved) {
            const parsed: TabConfigItem[] = JSON.parse(saved);
            // Merge: keep saved order/visibility, append any new tabs not yet in saved config
            const savedValues = new Set(parsed.map(t => t.value));
            const merged = [...parsed];
            for (const tab of ALL_TABS) {
                if (!savedValues.has(tab.value)) {
                    merged.push({ value: tab.value, visible: true });
                }
            }
            // Remove entries no longer in ALL_TABS
            const allValues = new Set(ALL_TABS.map(t => t.value));
            return merged.filter(t => allValues.has(t.value));
        }
    } catch {
        // ignore
    }
    return DEFAULT_TAB_CONFIG;
};

const saveTabConfig = (pageKey: string, config: TabConfigItem[]) => {
    try {
        localStorage.setItem(getStorageKey(pageKey), JSON.stringify(config));
    } catch {
        // ignore
    }
};

export const useTabConfig = (pageKey: string) => {
    const [tabConfig, setTabConfig] = useState<TabConfigItem[]>(() => loadTabConfig(pageKey));

    const updateTabConfig = useCallback((config: TabConfigItem[]) => {
        setTabConfig(config);
        saveTabConfig(pageKey, config);
    }, [pageKey]);

    return { tabConfig, updateTabConfig };
};
